import { parseDateMaybe } from './crossCheckEngine';
import type { ExtractedFieldEntry, TaxDataSnapshot } from '../store/useFormCGIAStore';

/**
 * Exigibilité du contrôle technique pour un CTVO.
 *
 * Règle légale : le CT n'est obligatoire que si le véhicule a plus de
 * 4 ans (date de 1ère mise en circulation). Même au-delà de 4 ans, le
 * champ X.1 de la carte grise ("VISITE AVANT LE …") encore dans le futur
 * vaut dispense — le PV séparé n'est pas requis.
 *
 * Partagé entre la règle cross-check #7 (`ct_obligatoire_si_mec_4_ans`),
 * la checklist FormCGIAv2 et le calcul des `missingDocTypes` du recap —
 * sans ce partage, le formulaire réclamait un CT inexistant pour les
 * véhicules récents (document_request impossible à satisfaire).
 */

export const CT_AGE_THRESHOLD_YEARS = 4;

const MS_PER_YEAR = 1000 * 60 * 60 * 24 * 365.25;

export const yearsSince = (from: Date, to: Date): number => (to.getTime() - from.getTime()) / MS_PER_YEAR;

/**
 * Parse une date ISO (OCR : `YYYY-MM-DD`) OU française (MiSIV : `DD/MM/YYYY`,
 * cf. `formatDateToDDMMYYYY` côté misivController). `new Date('13/05/2022')`
 * échoue ou s'inverse en MM/DD selon le moteur — on désambiguïse explicitement.
 */
export const parseFrOrIsoDate = (value: string | null | undefined): Date | null => {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  const frMatch = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (frMatch) {
    const [, dd, mm, yyyy] = frMatch;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return parseDateMaybe(value);
};

interface CtRequirementInput {
  /** Date de 1ère MEC (OCR carte grise ou MiSIV) — format parsable par `new Date()`. */
  dateMecRaw: string | null | undefined;
  /** Champ X.1 de la carte grise (date de prochaine visite CT). */
  dateValiditeCtRaw: string | null | undefined;
  now: Date;
}

/**
 * `true` si un PV de contrôle technique doit être exigé.
 * MEC inconnue → conservateur : CT requis (le pôle GOD tranchera).
 */
export const isCtRequired = ({ dateMecRaw, dateValiditeCtRaw, now }: CtRequirementInput): boolean => {
  const mec = parseFrOrIsoDate(dateMecRaw);
  const isUnderAgeThreshold = mec !== null && yearsSince(mec, now) <= CT_AGE_THRESHOLD_YEARS;
  if (isUnderAgeThreshold) return false;

  const dateValiditeCt = parseFrOrIsoDate(dateValiditeCtRaw);
  const hasValidX1 = dateValiditeCt !== null && dateValiditeCt.getTime() > now.getTime();
  if (hasValidX1) return false;

  return true;
};

/**
 * Variante branchée sur l'état de session FormCGIA v2 : MiSIV est la source
 * de vérité pour la MEC (fetché dès l'étape 1, avant tout upload), l'OCR
 * carte grise sert de fallback ; le X.1 ne peut venir que de l'OCR.
 */
export const isCtRequiredFromSession = ({
  extractedFields,
  taxData,
  now,
}: {
  extractedFields: Record<string, ExtractedFieldEntry>;
  taxData: TaxDataSnapshot | null;
  now: Date;
}): boolean => {
  // MiSIV prioritaire, OCR en fallback — y compris quand la valeur MiSIV
  // est présente mais imparsable (on ne perd pas la dispense pour autant).
  const misivMec = taxData?.vehicleDetails?.dateMEC;
  const ocrMec = extractedFields['date_premiere_mec']?.field?.value;
  const mecCandidates = [misivMec, ocrMec].filter((c): c is string => typeof c === 'string');
  const dateMecRaw = mecCandidates.find((c) => parseFrOrIsoDate(c) !== null) ?? null;

  const ocrX1 = extractedFields['date_validite_ct']?.field?.value;
  const dateValiditeCtRaw = typeof ocrX1 === 'string' ? ocrX1 : null;

  return isCtRequired({ dateMecRaw, dateValiditeCtRaw, now });
};
